import { type DatabaseFieldType } from '@databases/types/database';
import React, { type FunctionComponent } from 'react';

import { type EditComponentProps } from './types.ts';

/**
 * A HOC that wraps a popover edit component with an early return check for Jira
 * work item details fields.
 * Only instantiates the popover component when the jira work item detail type
 * matches. This provides a performance optimization by avoiding unnecessary
 * hook calls.
 *
 * @param detailType - The Jira work item detail type to check (e.g., 'assignee', 'priority', 'status')
 * @param PopoverComponent - The popover component to render when field type matches
 * @returns A wrapped component that performs early return optimization
 */
export const withJiraWorkItemCheck = (
  detailType: string,
  PopoverComponent: FunctionComponent<
    EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>
  >
): FunctionComponent<EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>> => {
  const JiraFieldValueEdit: FunctionComponent<
    EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>
  > = (props) => {
    const isMatchingJiraDetail = props.field.settings.jiraField === detailType;

    if (!isMatchingJiraDetail) {
      return null;
    }

    return <PopoverComponent {...props} />;
  };

  JiraFieldValueEdit.displayName = `withJiraWorkItemCheck(${detailType})`;

  return JiraFieldValueEdit;
};
